<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ProductsAPI from 'dashboard/api/products';
import WebsiteLayout from '../components/WebsiteLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ProductForm from '../components/ProductForm.vue';
import TablePagination from '../components/TablePagination.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ResponsiveTable from '../components/ResponsiveTable.vue';

const { t } = useI18n();

const products = ref([]);
const loading = ref(false);
const showForm = ref(false);
const selectedProduct = ref(null);
const showDuplicateModal = ref(false);
const productToDuplicate = ref(null);
const duplicateCount = ref(1);

// Header controls
const searchQuery = ref('');
const showFilterMenu = ref(false);
const showSortMenu = ref(false);
const filterActive = ref(null);
const filterStock = ref(null);
const sortField = ref(null);
const sortOrder = ref('asc');

// Pagination
const currentPage = ref(1);
const itemsPerPage = ref(25);

const headerTitle = computed(() => t('WEBSITE.HEADER.PRODUCTS_TITLE'));

// Filtrage et tri (sans pagination)
const filteredAndSortedProducts = computed(() => {
  let result = [...products.value];

  // Recherche
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter(
      product =>
        product.name.toLowerCase().includes(query) ||
        product.sku?.toLowerCase().includes(query) ||
        product.category?.toLowerCase().includes(query) ||
        product.product_category?.name?.toLowerCase().includes(query)
    );
  }

  // Filtre actif/inactif
  if (filterActive.value !== null) {
    result = result.filter(product => product.active === filterActive.value);
  }

  // Filtre stock
  if (filterStock.value === 'in_stock') {
    result = result.filter(product => product.stock > 0);
  } else if (filterStock.value === 'low_stock') {
    result = result.filter(product => product.stock > 0 && product.stock <= 10);
  } else if (filterStock.value === 'out_of_stock') {
    result = result.filter(product => product.stock === 0);
  }

  // Tri
  if (sortField.value) {
    result.sort((a, b) => {
      let aVal = a[sortField.value];
      let bVal = b[sortField.value];

      if (sortField.value === 'price' || sortField.value === 'stock') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (sortField.value === 'updated_at') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      if (aVal < bVal) return sortOrder.value === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder.value === 'asc' ? 1 : -1;
      return 0;
    });
  }

  return result;
});

// Pagination
const startIndex = computed(() => (currentPage.value - 1) * itemsPerPage.value);

const endIndex = computed(() =>
  Math.min(
    startIndex.value + itemsPerPage.value,
    filteredAndSortedProducts.value.length
  )
);

// Résultats paginés (utilisé pour l'affichage)
const paginatedProducts = computed(() => {
  return filteredAndSortedProducts.value.slice(
    startIndex.value,
    endIndex.value
  );
});

// États
const isEmpty = computed(() => !loading.value && products.value.length === 0);

const hasNoSearchResults = computed(
  () =>
    !loading.value &&
    products.value.length > 0 &&
    filteredAndSortedProducts.value.length === 0 &&
    (searchQuery.value ||
      filterActive.value !== null ||
      filterStock.value !== null)
);

// Reset pagination when filters change
watch([searchQuery, filterActive, filterStock, sortField, itemsPerPage], () => {
  currentPage.value = 1;
});

const fetchProducts = async () => {
  loading.value = true;
  try {
    const response = await ProductsAPI.get();
    products.value = response.data;
  } catch (error) {
    useAlert(t('WEBSITE.PRODUCTS.API.ERROR.FETCH_FAILED'));
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchProducts();
});

const openCreateForm = () => {
  selectedProduct.value = null;
  showForm.value = true;
};

const openEditForm = product => {
  selectedProduct.value = product;
  showForm.value = true;
};

const closeForm = () => {
  showForm.value = false;
  selectedProduct.value = null;
};

const handleSave = async () => {
  await fetchProducts();
  closeForm();
};

const deleteProduct = async product => {
  // eslint-disable-next-line no-alert, no-restricted-globals
  if (!confirm(t('WEBSITE.PRODUCTS.CONFIRM.DELETE'))) return;

  try {
    await ProductsAPI.delete(product.id);
    useAlert(t('WEBSITE.PRODUCTS.API.SUCCESS.PRODUCT_DELETED'));
    await fetchProducts();
  } catch (error) {
    useAlert(t('WEBSITE.PRODUCTS.API.ERROR.DELETE_FAILED'));
  }
};

const openDuplicateModal = product => {
  productToDuplicate.value = product;
  duplicateCount.value = 1;
  showDuplicateModal.value = true;
};

const closeDuplicateModal = () => {
  showDuplicateModal.value = false;
  productToDuplicate.value = null;
  duplicateCount.value = 1;
};

const confirmDuplicate = async () => {
  if (!productToDuplicate.value || duplicateCount.value < 1) return;

  try {
    const count = parseInt(duplicateCount.value, 10);
    const timestamp = Date.now();

    for (let i = 0; i < count; i += 1) {
      // Créer une copie du produit avec un nom et SKU modifiés
      const duplicatedProduct = {
        ...productToDuplicate.value,
        name:
          count > 1
            ? `${productToDuplicate.value.name} (Copy ${i + 1})`
            : `${productToDuplicate.value.name} (Copy)`,
        sku: productToDuplicate.value.sku
          ? `${productToDuplicate.value.sku}-${timestamp}-${i + 1}`
          : '',
        active: false, // Désactiver par défaut
      };

      // Supprimer les propriétés qui ne doivent pas être copiées
      delete duplicatedProduct.id;
      delete duplicatedProduct.created_at;
      delete duplicatedProduct.updated_at;
      delete duplicatedProduct.images; // Les images ne sont pas copiées
      delete duplicatedProduct.datasheet; // Les fiches PDF ne sont pas copiées

      // eslint-disable-next-line no-await-in-loop
      await ProductsAPI.create(duplicatedProduct);
    }

    const message =
      count > 1
        ? t('WEBSITE.PRODUCTS.API.SUCCESS.PRODUCTS_DUPLICATED', { count })
        : t('WEBSITE.PRODUCTS.API.SUCCESS.PRODUCT_DUPLICATED');

    useAlert(message);
    await fetchProducts();
    closeDuplicateModal();
  } catch (error) {
    useAlert(t('WEBSITE.PRODUCTS.API.ERROR.DUPLICATE_FAILED'));
  }
};

const formatPrice = (price, currency = 'EUR', priceUnit = null) => {
  const formattedPrice = new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency || 'EUR',
  }).format(price);

  return priceUnit ? `${formattedPrice} / ${priceUnit}` : formattedPrice;
};

const formatDate = dateString => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString();
};

// Fermer tous les menus sauf celui spécifié
const closeAllMenusExcept = exceptMenu => {
  if (exceptMenu !== 'filter') showFilterMenu.value = false;
  if (exceptMenu !== 'sort') showSortMenu.value = false;
};

// Gestion filtres et tri
const handleFilter = (type, value) => {
  if (type === 'active') {
    filterActive.value = value;
  } else if (type === 'stock') {
    filterStock.value = value;
  }
  showFilterMenu.value = false;
};

const handleSort = field => {
  if (sortField.value === field) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortField.value = field;
    sortOrder.value = 'asc';
  }
  showSortMenu.value = false;
};

const clearSearch = () => {
  searchQuery.value = '';
  filterActive.value = null;
  filterStock.value = null;
  sortField.value = null;
  sortOrder.value = 'asc';
  currentPage.value = 1;
};
</script>

<template>
  <WebsiteLayout :header-title="headerTitle">
    <template #actions>
      <Button
        :label="t('WEBSITE.PRODUCTS.EMPTY_STATE.BUTTON_LABEL')"
        icon="i-lucide-plus"
        size="sm"
        @click="openCreateForm"
      />
    </template>

    <!-- Loading -->
    <div
      v-if="loading"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>

    <!-- Empty state -->
    <div
      v-else-if="isEmpty"
      class="flex flex-col items-center justify-center py-24 rounded-lg bg-gradient-to-br from-n-solid-1 to-n-slate-2 dark:to-n-slate-1 border-2 border-dashed border-n-weak"
    >
      <div
        class="mb-6 w-20 h-20 rounded-full bg-n-purple-2 dark:bg-n-purple-3 flex items-center justify-center"
      >
        <span class="i-lucide-package size-10 text-n-purple-10" />
      </div>
      <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
        {{ t('WEBSITE.PRODUCTS.EMPTY_STATE.TITLE') }}
      </h3>
      <p class="text-sm text-center text-n-slate-10 max-w-md mb-6">
        {{ t('WEBSITE.PRODUCTS.EMPTY_STATE.SUBTITLE') }}
      </p>
      <Button
        :label="t('WEBSITE.PRODUCTS.EMPTY_STATE.BUTTON_LABEL')"
        icon="i-lucide-plus"
        size="sm"
        @click="openCreateForm"
      />
    </div>

    <!-- Header bar avec search, filtres, tri -->
    <div
      v-else-if="!isEmpty"
      class="mb-3 bg-n-solid-1 border border-n-weak/70 rounded-lg shadow-sm"
    >
      <div class="flex items-center gap-2 px-2.5 py-1.5">
        <!-- Search bar -->
        <div class="flex-1 max-w-sm">
          <Input
            v-model="searchQuery"
            type="search"
            :placeholder="t('WEBSITE.PRODUCTS.LIST.SEARCH_PLACEHOLDER')"
            :custom-input-class="[
              'h-8 [&:not(.focus)]:!border-transparent [&:not(.focus)]:!outline-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8 !text-xs',
            ]"
          >
            <template #prefix>
              <Icon
                icon="i-lucide-search"
                class="absolute -translate-y-1/2 text-n-slate-11 size-3.5 top-1/2 ltr:left-2 rtl:right-2 pointer-events-none"
              />
            </template>
          </Input>
        </div>

        <!-- Séparateur vertical -->
        <div class="h-6 w-px bg-n-weak/40" />

        <!-- Filtres, Tri -->
        <div class="flex items-center gap-1.5">
          <!-- Filtre -->
          <div class="relative">
            <button
              class="px-2.5 py-1.5 bg-n-solid-1 border border-n-weak/50 text-n-slate-11 text-xs hover:bg-n-blue-2 hover:text-n-blue-11 hover:border-n-blue-6 transition-all flex items-center gap-1.5 rounded-lg font-medium"
              :class="{
                'bg-n-blue-3 text-n-blue-11 border-n-blue-6':
                  filterActive !== null || filterStock !== null,
              }"
              @click="
                closeAllMenusExcept('filter');
                showFilterMenu = !showFilterMenu;
              "
            >
              <span class="i-lucide-filter size-3.5" />
              <span>{{ t('WEBSITE.PRODUCTS.LIST.FILTER') }}</span>
              <span
                v-if="filterActive !== null || filterStock !== null"
                class="w-1.5 h-1.5 bg-n-blue-9 rounded-full animate-pulse"
              />
            </button>

            <div
              v-if="showFilterMenu"
              v-on-clickaway="() => (showFilterMenu = false)"
              class="absolute right-0 mt-1.5 w-56 bg-n-solid-1 border border-n-weak/60 shadow-2xl rounded-xl z-[100] py-2.5 overflow-hidden"
            >
              <!-- Filtre statut -->
              <div
                class="px-3 py-1 text-[10px] font-semibold text-n-slate-10 uppercase tracking-wide"
              >
                {{ t('WEBSITE.PRODUCTS.LIST.STATUS') }}
              </div>
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-blue-2 text-n-blue-11': filterActive === null,
                }"
                @click="handleFilter('active', null)"
              >
                <span class="font-medium">{{
                  t('WEBSITE.PRODUCTS.LIST.ALL_PRODUCTS')
                }}</span>
                <span
                  v-if="filterActive === null"
                  class="i-lucide-check size-3.5 text-n-blue-11"
                />
              </button>
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-teal-2 text-n-teal-11': filterActive === true,
                }"
                @click="handleFilter('active', true)"
              >
                <span class="font-medium flex items-center gap-2.5">
                  <span
                    class="w-2 h-2 rounded-full bg-n-teal-9 group-hover:scale-110 transition-transform"
                  />
                  {{ t('WEBSITE.PRODUCTS.LIST.ACTIVE') }}
                </span>
                <span
                  v-if="filterActive === true"
                  class="i-lucide-check size-3.5 text-n-teal-11"
                />
              </button>
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-slate-2 text-n-slate-11': filterActive === false,
                }"
                @click="handleFilter('active', false)"
              >
                <span class="font-medium flex items-center gap-2.5">
                  <span
                    class="w-2 h-2 rounded-full bg-n-slate-9 group-hover:scale-110 transition-transform"
                  />
                  {{ t('WEBSITE.PRODUCTS.LIST.INACTIVE') }}
                </span>
                <span
                  v-if="filterActive === false"
                  class="i-lucide-check size-3.5 text-n-slate-11"
                />
              </button>

              <div class="h-px bg-n-weak/30 my-2 mx-3" />

              <!-- Filtre stock -->
              <div
                class="px-3 py-1 text-[10px] font-semibold text-n-slate-10 uppercase tracking-wide"
              >
                {{ t('WEBSITE.PRODUCTS.LIST.STOCK') }}
              </div>
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-blue-2 text-n-blue-11': filterStock === null,
                }"
                @click="handleFilter('stock', null)"
              >
                <span class="font-medium">{{
                  t('WEBSITE.PRODUCTS.LIST.ALL_STOCK')
                }}</span>
                <span
                  v-if="filterStock === null"
                  class="i-lucide-check size-3.5 text-n-blue-11"
                />
              </button>
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-teal-2 text-n-teal-11': filterStock === 'in_stock',
                }"
                @click="handleFilter('stock', 'in_stock')"
              >
                <span class="font-medium flex items-center gap-2.5">
                  <span
                    class="w-2 h-2 rounded-full bg-n-teal-9 group-hover:scale-110 transition-transform"
                  />
                  {{ t('WEBSITE.PRODUCTS.LIST.IN_STOCK') }}
                </span>
                <span
                  v-if="filterStock === 'in_stock'"
                  class="i-lucide-check size-3.5 text-n-teal-11"
                />
              </button>
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-orange-2 text-n-orange-11': filterStock === 'low_stock',
                }"
                @click="handleFilter('stock', 'low_stock')"
              >
                <span class="font-medium flex items-center gap-2.5">
                  <span
                    class="w-2 h-2 rounded-full bg-n-orange-9 group-hover:scale-110 transition-transform"
                  />
                  {{ t('WEBSITE.PRODUCTS.LIST.LOW_STOCK') }}
                </span>
                <span
                  v-if="filterStock === 'low_stock'"
                  class="i-lucide-check size-3.5 text-n-orange-11"
                />
              </button>
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-ruby-2 text-n-ruby-11': filterStock === 'out_of_stock',
                }"
                @click="handleFilter('stock', 'out_of_stock')"
              >
                <span class="font-medium flex items-center gap-2.5">
                  <span
                    class="w-2 h-2 rounded-full bg-n-ruby-9 group-hover:scale-110 transition-transform"
                  />
                  {{ t('WEBSITE.PRODUCTS.LIST.OUT_OF_STOCK') }}
                </span>
                <span
                  v-if="filterStock === 'out_of_stock'"
                  class="i-lucide-check size-3.5 text-n-ruby-11"
                />
              </button>
            </div>
          </div>

          <!-- Tri -->
          <div class="relative">
            <button
              class="px-2.5 py-1.5 bg-n-solid-1 border border-n-weak/50 text-n-slate-11 text-xs hover:bg-n-blue-2 hover:text-n-blue-11 hover:border-n-blue-6 transition-all flex items-center gap-1.5 rounded-lg font-medium"
              :class="{
                'bg-n-blue-3 text-n-blue-11 border-n-blue-6': sortField,
              }"
              @click="
                closeAllMenusExcept('sort');
                showSortMenu = !showSortMenu;
              "
            >
              <span class="i-lucide-arrow-up-down size-3.5" />
              <span>{{ t('WEBSITE.PRODUCTS.LIST.SORT') }}</span>
              <span
                v-if="sortField"
                class="w-1.5 h-1.5 bg-n-blue-9 rounded-full animate-pulse"
              />
            </button>

            <div
              v-if="showSortMenu"
              v-on-clickaway="() => (showSortMenu = false)"
              class="absolute right-0 mt-1.5 w-60 bg-n-solid-1 border border-n-weak/60 shadow-2xl rounded-xl z-[100] py-2.5 overflow-hidden"
            >
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-blue-2 text-n-blue-11': sortField === 'name',
                }"
                @click="handleSort('name')"
              >
                <span class="flex items-center gap-2.5 font-medium">
                  <span
                    class="i-lucide-type size-3.5 group-hover:scale-110 transition-transform"
                  />
                  <span>{{ t('WEBSITE.PRODUCTS.LIST.SORT_NAME') }}</span>
                </span>
                <span
                  v-if="sortField === 'name'"
                  :class="
                    sortOrder === 'asc'
                      ? 'i-lucide-arrow-up'
                      : 'i-lucide-arrow-down'
                  "
                  class="size-3.5 text-n-blue-11"
                />
              </button>
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-blue-2 text-n-blue-11': sortField === 'price',
                }"
                @click="handleSort('price')"
              >
                <span class="flex items-center gap-2.5 font-medium">
                  <span
                    class="i-lucide-euro size-3.5 group-hover:scale-110 transition-transform"
                  />
                  <span>{{ t('WEBSITE.PRODUCTS.LIST.SORT_PRICE') }}</span>
                </span>
                <span
                  v-if="sortField === 'price'"
                  :class="
                    sortOrder === 'asc'
                      ? 'i-lucide-arrow-up'
                      : 'i-lucide-arrow-down'
                  "
                  class="size-3.5 text-n-blue-11"
                />
              </button>
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-blue-2 text-n-blue-11': sortField === 'stock',
                }"
                @click="handleSort('stock')"
              >
                <span class="flex items-center gap-2.5 font-medium">
                  <span
                    class="i-lucide-package size-3.5 group-hover:scale-110 transition-transform"
                  />
                  <span>{{ t('WEBSITE.PRODUCTS.LIST.SORT_STOCK') }}</span>
                </span>
                <span
                  v-if="sortField === 'stock'"
                  :class="
                    sortOrder === 'asc'
                      ? 'i-lucide-arrow-up'
                      : 'i-lucide-arrow-down'
                  "
                  class="size-3.5 text-n-blue-11"
                />
              </button>
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-blue-2 text-n-blue-11': sortField === 'updated_at',
                }"
                @click="handleSort('updated_at')"
              >
                <span class="flex items-center gap-2.5 font-medium">
                  <span
                    class="i-lucide-calendar size-3.5 group-hover:scale-110 transition-transform"
                  />
                  <span>{{ t('WEBSITE.PRODUCTS.LIST.SORT_DATE') }}</span>
                </span>
                <span
                  v-if="sortField === 'updated_at'"
                  :class="
                    sortOrder === 'asc'
                      ? 'i-lucide-arrow-up'
                      : 'i-lucide-arrow-down'
                  "
                  class="size-3.5 text-n-blue-11"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- No search results -->
    <div
      v-if="hasNoSearchResults"
      class="flex flex-col items-center justify-center py-16 bg-n-solid-1 border border-n-weak/70 rounded-lg"
    >
      <span class="i-lucide-search-x mb-4 text-n-slate-9 size-12" />
      <h3 class="text-base font-semibold text-center text-n-slate-12 mb-2">
        {{ t('WEBSITE.PRODUCTS.NO_RESULTS.TITLE') }}
      </h3>
      <p class="text-sm text-center text-n-slate-10 max-w-md mb-6">
        {{ t('WEBSITE.PRODUCTS.NO_RESULTS.SUBTITLE') }}
      </p>
      <Button
        :label="t('WEBSITE.PRODUCTS.NO_RESULTS.CLEAR_BUTTON')"
        icon="i-lucide-x"
        size="sm"
        variant="outline"
        color="slate"
        @click="clearSearch"
      />
    </div>

    <!-- Products list -->
    <div
      v-else-if="!isEmpty && !hasNoSearchResults"
      class="overflow-hidden border rounded-lg border-n-weak/70 bg-n-solid-1 shadow-sm min-h-[400px]"
    >
      <ResponsiveTable>
        <table class="w-full">
          <thead class="bg-n-alpha-1/50 border-b border-n-weak/60">
            <tr>
              <th
                class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
              >
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-package size-3.5 opacity-40" />
                  <span>{{ t('WEBSITE.PRODUCTS.LIST.NAME') }}</span>
                </div>
              </th>
              <th
                class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
              >
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-hash size-3.5 opacity-40" />
                  <span>{{ t('WEBSITE.PRODUCTS.LIST.SKU') }}</span>
                </div>
              </th>
              <th
                class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
              >
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-euro size-3.5 opacity-40" />
                  <span>{{ t('WEBSITE.PRODUCTS.LIST.PRICE') }}</span>
                </div>
              </th>
              <th
                class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
              >
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-layers size-3.5 opacity-40" />
                  <span>{{ t('WEBSITE.PRODUCTS.LIST.STOCK') }}</span>
                </div>
              </th>
              <th
                class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
              >
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-activity size-3.5 opacity-40" />
                  <span>{{ t('WEBSITE.PRODUCTS.LIST.STATUS') }}</span>
                </div>
              </th>
              <th
                class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
              >
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-calendar size-3.5 opacity-40" />
                  <span>{{ t('WEBSITE.PRODUCTS.LIST.UPDATED') }}</span>
                </div>
              </th>
              <th
                class="sticky right-0 px-5 py-3 text-xs font-semibold text-right tracking-wide text-n-slate-11 bg-n-alpha-1/50 backdrop-blur-sm shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.1)]"
              >
                <div class="flex items-center justify-end gap-1.5">
                  <span>{{ t('WEBSITE.PRODUCTS.LIST.ACTIONS') }}</span>
                  <span class="i-lucide-more-horizontal size-3.5 opacity-30" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody class="bg-n-solid-1">
            <tr
              v-for="product in paginatedProducts"
              :key="product.id"
              class="transition-all duration-150 ease-out border-t border-n-weak/40 hover:bg-n-blue-2/50 group"
            >
              <td class="px-5 py-3">
                <div class="flex items-center gap-3">
                  <div class="relative">
                    <div
                      class="w-10 h-10 rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0"
                      :class="[
                        product.images && product.images.length > 0
                          ? 'bg-n-solid-1 border border-n-weak'
                          : 'bg-n-alpha-1',
                      ]"
                    >
                      <img
                        v-if="product.images && product.images.length > 0"
                        :src="product.images[0].url"
                        :alt="product.name"
                        class="w-full h-full object-cover"
                      />
                      <span
                        v-else
                        class="i-lucide-package size-5 text-n-slate-10"
                      />
                    </div>
                    <!-- Indicateur PDF -->
                    <a
                      v-if="product.datasheet"
                      :href="product.datasheet.url"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="absolute -bottom-1 -right-1 w-5 h-5 rounded bg-n-red-9 flex items-center justify-center shadow-md hover:bg-n-red-10 transition-colors"
                      :title="t('WEBSITE.PRODUCTS.FORM.DATASHEET_AVAILABLE')"
                      @click.stop
                    >
                      <span class="i-lucide-file-text size-3 text-white" />
                    </a>
                  </div>
                  <div class="min-w-0">
                    <span class="text-sm font-medium text-n-slate-12 block">
                      {{ product.name }}
                    </span>
                    <span class="text-xs text-n-slate-10">
                      {{ product.product_category?.name || '-' }}
                    </span>
                  </div>
                </div>
              </td>
              <td class="px-5 py-3 text-sm text-n-slate-10">
                {{ product.sku }}
              </td>
              <td class="px-5 py-3">
                <div class="text-sm font-medium text-n-slate-12">
                  {{
                    formatPrice(
                      product.price,
                      product.currency,
                      product.price_unit
                    )
                  }}
                </div>
              </td>
              <td class="px-5 py-3">
                <span
                  class="text-sm"
                  :class="[
                    product.stock > 10
                      ? 'text-green-600 dark:text-green-400'
                      : product.stock > 0
                        ? 'text-orange-600 dark:text-orange-400'
                        : 'text-red-600 dark:text-red-400',
                  ]"
                >
                  {{ product.stock }} {{ t('WEBSITE.PRODUCTS.LIST.UNITS') }}
                </span>
              </td>
              <td class="px-5 py-3">
                <div
                  class="inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-medium rounded-full border whitespace-nowrap transition-all duration-200"
                  :class="[
                    product.active
                      ? 'bg-n-teal-2 text-n-teal-11 border-n-teal-6'
                      : 'bg-n-slate-2 text-n-slate-11 border-n-slate-6',
                  ]"
                >
                  <div
                    class="w-1.5 h-1.5 rounded-full"
                    :class="[
                      product.active
                        ? 'bg-n-teal-9 animate-pulse'
                        : 'bg-n-slate-9',
                    ]"
                  />
                  {{
                    product.active
                      ? t('WEBSITE.PRODUCTS.LIST.ACTIVE')
                      : t('WEBSITE.PRODUCTS.LIST.INACTIVE')
                  }}
                </div>
              </td>
              <td class="px-5 py-3 text-sm text-n-slate-10">
                {{ formatDate(product.updated_at) }}
              </td>
              <td
                class="sticky right-0 px-5 py-3 bg-n-solid-1 backdrop-blur-sm shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.1)]"
              >
                <div class="flex justify-end gap-2">
                  <button
                    class="flex items-center justify-center w-8 h-8 rounded-md bg-n-blue-3 hover:bg-n-blue-4 text-n-blue-11 hover:text-n-blue-12 border border-n-blue-6 hover:border-n-blue-7 transition-all duration-200 shadow-sm hover:shadow-md"
                    :title="t('WEBSITE.PRODUCTS.FORM.EDIT')"
                    @click="openEditForm(product)"
                  >
                    <span class="i-lucide-edit size-4" />
                  </button>
                  <button
                    class="flex items-center justify-center w-8 h-8 rounded-md bg-n-purple-3 hover:bg-n-purple-4 text-n-purple-11 hover:text-n-purple-12 border border-n-purple-6 hover:border-n-purple-7 transition-all duration-200 shadow-sm hover:shadow-md"
                    :title="t('WEBSITE.PRODUCTS.FORM.DUPLICATE')"
                    @click="openDuplicateModal(product)"
                  >
                    <span class="i-lucide-copy size-4" />
                  </button>
                  <button
                    class="flex items-center justify-center w-8 h-8 rounded-md bg-n-ruby-3 hover:bg-n-ruby-4 text-n-ruby-11 hover:text-n-ruby-12 border border-n-ruby-6 hover:border-n-ruby-7 transition-all duration-200 shadow-sm hover:shadow-md"
                    :title="t('WEBSITE.PRODUCTS.FORM.DELETE')"
                    @click="deleteProduct(product)"
                  >
                    <span class="i-lucide-trash-2 size-4" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </ResponsiveTable>
    </div>

    <!-- Pagination Footer -->
    <div
      v-if="
        !isEmpty && !hasNoSearchResults && filteredAndSortedProducts.length > 0
      "
      class="mt-6"
    >
      <TablePagination
        :current-page="currentPage"
        :total-items="filteredAndSortedProducts.length"
        :items-per-page="itemsPerPage"
        @update:current-page="currentPage = $event"
        @update:items-per-page="itemsPerPage = $event"
      />
    </div>

    <!-- Product Form Drawer -->
    <ProductForm
      v-if="showForm"
      :product="selectedProduct"
      @close="closeForm"
      @save="handleSave"
    />

    <!-- Duplicate Modal -->
    <Dialog
      v-if="showDuplicateModal"
      :title="t('WEBSITE.PRODUCTS.DUPLICATE.TITLE')"
      :cancel-button-label="t('WEBSITE.PRODUCTS.DUPLICATE.CANCEL')"
      :confirm-button-label="t('WEBSITE.PRODUCTS.DUPLICATE.CONFIRM')"
      @close="closeDuplicateModal"
      @confirm="confirmDuplicate"
    >
      <div class="space-y-4">
        <p class="text-sm text-n-slate-11">
          {{ t('WEBSITE.PRODUCTS.DUPLICATE.MESSAGE') }}
        </p>
        <div>
          <label
            class="block text-sm font-medium text-n-slate-12 mb-2"
            for="duplicate-count"
          >
            {{ t('WEBSITE.PRODUCTS.DUPLICATE.QUANTITY') }}
          </label>
          <Input
            id="duplicate-count"
            v-model.number="duplicateCount"
            type="number"
            min="1"
            max="100"
            :placeholder="t('WEBSITE.PRODUCTS.DUPLICATE.QUANTITY_PLACEHOLDER')"
          />
        </div>
      </div>
    </Dialog>
  </WebsiteLayout>
</template>
